import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { publicApi } from '@/lib/api';
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, Youtube, Linkedin, Globe, MessageCircle, Music2, Send, Settings } from 'lucide-react';
import { Twitch, X as XIcon } from 'lucide-react';
import { useLanguage } from '@/lib/i18n';

const DAY_NAMES = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

function LangToggle() {
  const { lang, setLang } = useLanguage();
  const langs = ['fr', 'en', 'es'];
  return (
    <div className="flex items-center rounded-full border border-white/20 overflow-hidden text-xs font-medium" data-testid="maintenance-lang-switch">
      {langs.map(l => (
        <button key={l} onClick={() => setLang(l)} className={`px-2.5 py-1 transition-colors ${lang === l ? 'bg-white text-garden-800' : 'text-white/70 hover:bg-white/10'}`}>{l.toUpperCase()}</button>
      ))}
    </div>
  );
}

const ICON_MAP = {
  instagram: Instagram, facebook: Facebook, twitter: XIcon, youtube: Youtube,
  tiktok: Music2, linkedin: Linkedin, whatsapp: MessageCircle, telegram: Send,
  twitch: Twitch, discord: MessageCircle, tripadvisor: MapPin,
};

export default function MaintenancePage() {
  const [data, setData] = useState(null);
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicApi.getMaintenanceInfo()
      .then(r => setData(r.data))
      .catch(() => setData({ maintenance_mode: true, name: 'Restaurant', maintenance_message: 'Site en cours de mise à jour.' }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-garden-800 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const formatSlots = (slots) => slots?.map(s => `${s.start} - ${s.end}`).join(', ') || '';
  const logoUrl = data.restaurant_logo_url
    ? (data.restaurant_logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${data.restaurant_logo_url}` : data.restaurant_logo_url)
    : null;

  return (
    <div className="min-h-screen bg-garden-800 text-white" data-testid="maintenance-page">
      <title>{data.meta_title || data.name || 'Restaurant'} - Maintenance</title>

      {/* Language selector */}
      <div className="fixed top-4 right-4 z-50">
        <LangToggle />
      </div>

      <div className="max-w-2xl mx-auto px-6 py-16 flex flex-col items-center text-center">
        {/* Logo or Restaurant Name */}
        <div className="mb-8">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={data.name || 'Restaurant'}
              className="h-20 w-auto object-contain mx-auto mb-4"
              data-testid="maintenance-logo"
            />
          ) : null}
          <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-3" data-testid="maintenance-name">
            {data.name || 'Restaurant'}
          </h1>
          {data.slogan && (
            <p className="text-lg text-white/70 font-light italic">{data.slogan}</p>
          )}
        </div>

        {/* Maintenance Message */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-10 w-full border border-white/10" data-testid="maintenance-message">
          <div className="w-12 h-12 bg-sunny/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">&#9888;</span>
          </div>
          <p className="text-lg font-medium mb-2">{t('maintenance.comingSoon')}</p>
          <p className="text-white/70 text-sm">
            {data.maintenance_message || t('maintenance.defaultMessage')}
          </p>
        </div>

        {/* Contact Info */}
        <div className="w-full space-y-3 mb-10">
          {data.address && (
            <a href={data.maps_url || `https://maps.google.com/?q=${encodeURIComponent(data.address)}`} target="_blank" rel="noreferrer"
              className="flex items-center gap-3 bg-white/5 hover:bg-white/10 transition-colors rounded-xl px-5 py-3 text-left" data-testid="maint-address">
              <MapPin size={18} className="text-sunny shrink-0" />
              <span className="text-sm">{data.address}</span>
            </a>
          )}
          {data.phone && (
            <a href={`tel:${data.phone.replace(/\s/g, '')}`}
              className="flex items-center gap-3 bg-white/5 hover:bg-white/10 transition-colors rounded-xl px-5 py-3" data-testid="maint-phone">
              <Phone size={18} className="text-sunny shrink-0" />
              <span className="text-sm font-medium">{data.phone}</span>
            </a>
          )}
          {data.email && (
            <a href={`mailto:${data.email}`}
              className="flex items-center gap-3 bg-white/5 hover:bg-white/10 transition-colors rounded-xl px-5 py-3" data-testid="maint-email">
              <Mail size={18} className="text-sunny shrink-0" />
              <span className="text-sm">{data.email}</span>
            </a>
          )}
        </div>

        {/* Hours */}
        {data.hours && data.hours.length > 0 && (
          <div className="w-full bg-white/5 rounded-2xl p-6 mb-10 border border-white/10" data-testid="maint-hours">
            <h2 className="flex items-center justify-center gap-2 font-heading font-semibold text-lg mb-4">
              <Clock size={18} className="text-sunny" /> {t('maintenance.openingHours')}
            </h2>
            <div className="space-y-2">
              {data.hours.map((h) => (
                <div key={h.day} className="flex justify-between text-sm px-2">
                  <span className="font-medium">{h.day_name || DAY_NAMES[h.day]}</span>
                  <span className={h.is_closed ? 'text-red-300' : 'text-white/70'}>
                    {h.is_closed ? t('maintenance.closed') : formatSlots(h.slots)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Map */}
        {data.maps_url && (
          <div className="w-full rounded-2xl overflow-hidden mb-10 border border-white/10" data-testid="maint-map">
            <iframe
              title="Localisation"
              src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2829.7!2d-0.5676!3d44.8378!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDTCsDUwJzE2LjEiTiAwwrAzNCcwMy40Ilc!5e0!3m2!1sfr!2sfr`}
              width="100%" height="200" style={{ border: 0 }} allowFullScreen loading="lazy"
            />
          </div>
        )}

        {/* Social Links */}
        {data.social_links && data.social_links.length > 0 && (
          <div className="flex flex-wrap justify-center gap-3 mb-10" data-testid="maint-social">
            {data.social_links.map((link, i) => {
              const IconComp = ICON_MAP[link.icon_key] || Globe;
              return (
                <a key={link.url || i} href={link.url} target="_blank" rel="noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                  title={link.name}>
                  <IconComp size={18} />
                </a>
              );
            })}
          </div>
        )}

        {/* Footer with admin link */}
        <div className="mt-8 space-y-2">
          <p className="text-xs text-white/30">
            &copy; {new Date().getFullYear()} {data.name}. {t('maintenance.copyright')}
          </p>
          <Link
            to="/admin"
            className="inline-flex items-center gap-1 text-[10px] text-white/15 hover:text-white/40 transition-colors"
            data-testid="maintenance-admin-link"
          >
            <Settings size={10} />
            Administration
          </Link>
        </div>
      </div>
    </div>
  );
}
